import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaperPlaneTilt,
  Archive, ArrowCounterClockwise, CaretLeft, CaretRight, ChartPieSlice, Door, DotsThree, FileCsv, FilePdf, FolderSimple, HouseLine, MapPin,
  MicrosoftExcelLogo, PencilSimple, Plus, Receipt, Table, Trash,
} from '@phosphor-icons/react';
import { useNotification } from '../../../../contexts/NotificationContext';
import { BestaetigenDialog, Chip, MenueEintrag, MenueTitel, Popover } from '../../../../components/immobilien/ui';
import { istArchiviert, istVerknuepfungsFehler, setzeArchiviert } from '../../../../lib/immobilien/archiv';
import { objektTransitionName } from '../../../../lib/immobilien/viewTransition';
import { loescheObjekt } from '../../../../lib/immobilien/useObjektListe';
import { meldeObjektGeaendert } from '../../../../lib/immobilien/useObjektKontext';
import { eur, qm } from '../../../../lib/immobilien/format';
import { fotoHintergrund } from '../../../../lib/immobilien/url';
import { exportCsv } from '../../../../lib/export/exportCsv';
import { exportXlsx } from '../../../../lib/export/exportXlsx';
import { exportPdfReport, exportPdfTable } from '../../../../lib/export/exportPdf';
import { generateFileName } from '../../../../lib/export/types';
import type { ExportColumn } from '../../../../lib/export/types';
import type { EinheitUebersicht } from '../../../../lib/immobilien/objektDetail';
import { useObjektSeite } from './ObjektSeitenKontext';

/** Einheiten-Export (wie bisher über „Export“ der Objektseite). */
const EINHEITEN_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'bezeichnung', label: 'Bezeichnung' },
  { key: 'typ', label: 'Typ' },
  { key: 'flaeche', label: 'Wohnfläche', format: (v) => (typeof v === 'number' && v > 0 ? qm(v) : '') },
  { key: 'etage', label: 'Etage' },
  { key: 'status', label: 'Status' },
  { key: 'mieter', label: 'Mieter' },
  { key: 'gesamt', label: 'Gesamtmiete (€)', format: (v) => (typeof v === 'number' && v > 0 ? eur(v).replace(' €', '') : '') },
];

/** Kopf-Knopf (36 px, Fläche --fill2 mit Linie). */
export function KopfKnopf({ icon: IconKomp, children, ...rest }: React.ComponentPropsWithRef<'button'> & { icon: typeof Door }) {
  return (
    <button type="button" {...rest} className="immo-hover-flaeche flex items-center whitespace-nowrap cursor-pointer"
      style={{ height: 36, padding: '0 14px', borderRadius: 18, border: 'none', gap: 7, background: 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
      <IconKomp size={15} />
      {children}
    </button>
  );
}

export function Breadcrumb({ zusatz }: { zusatz?: React.ReactNode }) {
  const navigate = useNavigate();
  const { name, daten } = useObjektSeite();
  return (
    <nav aria-label="Brotkrumen" className="flex items-center flex-wrap min-w-0" style={{ gap: 10 }}>
      <button type="button" onClick={() => navigate('/admin/objekte')} className="immo-glas flex items-center cursor-pointer"
        style={{ height: 34, padding: '0 14px 0 10px', borderRadius: 17, border: 'none', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
        <CaretLeft weight="bold" size={13} />
        Immobilien
      </button>
      <span className="flex items-center min-w-0" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>
        <CaretRight weight="bold" size={10} color="var(--ink3)" />
        <button type="button" onClick={() => navigate(`/admin/objekte/${encodeURIComponent(daten.objekt.id)}`)} className="whitespace-nowrap cursor-pointer"
          style={{ border: 'none', background: 'transparent', padding: 0, fontWeight: 600, color: zusatz ? 'var(--acc)' : 'var(--ink)' }}>
          {name}
        </button>
        {zusatz}
      </span>
    </nav>
  );
}

export function ObjektHero() {
  const navigate = useNavigate();
  const { showToast } = useNotification();
  const { daten, name, adresse, kennzahlen, uebersicht, neuLaden, oeffnePanel, zuEinheiten } = useObjektSeite();
  const [loeschen, setLoeschen] = useState(false);
  const [archivDialog, setArchivDialog] = useState(false);
  const [loeschtGerade, setLoeschtGerade] = useState(false);
  const o = daten.objekt;
  const haeuser = daten.haeuser.length;

  const nachAenderung = () => { neuLaden(); meldeObjektGeaendert(o.id); };

  // Häuser im Einheiten-Tab (neues Design): verwalten bzw. erstes Haus anlegen
  const haeuserFormular = () => navigate(`/admin/objekte/${encodeURIComponent(o.id)}/einheiten?${haeuser > 0 ? 'haeuser=1' : 'neu=haus'}`);
  const berichtscenter = () => oeffnePanel('bericht');
  // „Neue Einheit“ im neuen Design (Einheiten-Tab, README §5.4)
  const neueEinheit = () => navigate(`/admin/objekte/${encodeURIComponent(o.id)}/einheiten?neu=1`);

  const exportiere = (format: 'csv' | 'xlsx' | 'pdf-table' | 'pdf-report') => {
    const roh = new Map(daten.einheiten.map(e => [e.id, e]));
    const zeilen = uebersicht.map((e: EinheitUebersicht) => ({
      bezeichnung: e.bezeichnung,
      typ: roh.get(e.id)?.typ ?? '',
      flaeche: e.flaeche,
      etage: roh.get(e.id)?.etage ?? '',
      status: e.vermietet ? 'vermietet' : 'leerstand',
      mieter: e.mieter.join(', '),
      gesamt: e.gesamt,
    }));
    const config = {
      title: `${name} – Einheiten`,
      columns: EINHEITEN_EXPORT_COLUMNS,
      data: zeilen,
      fileName: generateFileName(`${name.replace(/\s+/g, '_')}_Einheiten`),
      stats: { items: [
        { label: 'Einheiten', value: kennzahlen.einheiten },
        { label: 'Vermietet', value: kennzahlen.vermietet },
        { label: 'Frei', value: kennzahlen.frei },
      ] },
    };
    try {
      if (format === 'csv') exportCsv(config);
      else if (format === 'xlsx') exportXlsx(config);
      else if (format === 'pdf-table') exportPdfTable(config);
      else exportPdfReport(config);
    } catch (e) {
      showToast({ type: 'error', message: `Export fehlgeschlagen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}` });
    }
  };

  const archiviert = istArchiviert(o.status);
  const bestaetigeArchiv = async () => {
    setLoeschtGerade(true);
    try {
      await setzeArchiviert(o.id, !archiviert);
      showToast({ type: 'success', message: archiviert ? `${name} wiederhergestellt` : `${name} archiviert` });
      setArchivDialog(false);
      neuLaden('objekt');
      meldeObjektGeaendert(o.id);
    } catch (e) {
      showToast({ type: 'error', message: `Nicht gespeichert: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}` });
    } finally {
      setLoeschtGerade(false);
    }
  };

  const bestaetigeLoeschen = async () => {
    setLoeschtGerade(true);
    try {
      await loescheObjekt(o.id);
      showToast({ type: 'success', message: `${name} gelöscht` });
      navigate('/admin/objekte', { replace: true });
    } catch (e) {
      const meldung = e instanceof Error ? e.message : 'Unbekannter Fehler';
      showToast({ type: 'error', message: istVerknuepfungsFehler(meldung)
        ? `${name} hat verknüpfte Daten und kann nicht gelöscht werden. Archiviere das Objekt stattdessen.`
        : `Fehler beim Löschen des Objekts: ${meldung}` });
      setLoeschtGerade(false);
      setLoeschen(false);
      if (istVerknuepfungsFehler(meldung) && !archiviert) setArchivDialog(true);
    }
  };

  const wohnLabel = kennzahlen.wohnungen === 1 ? '1 Wohnung' : `${kennzahlen.wohnungen} Wohnungen`;
  const foto = fotoHintergrund(o.bild_url);

  return (
    <header
      className="relative flex justify-between items-end flex-wrap"
      style={{
        viewTransitionName: objektTransitionName(o.id),
        zIndex: 6, minHeight: 240, borderRadius: 30, padding: 24, gap: 14,
        background: foto ?? 'var(--fill3)',
        boxShadow: '0 20px 50px var(--shade), inset 0 0 0 1px var(--line)',
      }}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        borderRadius: 'inherit',
        background: 'repeating-linear-gradient(135deg,var(--fill) 0 12px,transparent 12px 24px), linear-gradient(180deg,transparent 15%,color-mix(in srgb,var(--bg) 88%,transparent) 92%)',
      }} />
      <div className="relative flex flex-col min-w-0" style={{ gap: 4, flex: '1 1 300px' }}>
        {archiviert && <span className="self-start"><Chip ton="warn" groesse="s" icon={Archive}>Archiviert</Chip></span>}
        <h1 className="m-0" style={{ fontSize: 40, lineHeight: 1.08, fontWeight: 700, letterSpacing: '-0.035em', textWrap: 'balance' }}>{name}</h1>
        <span className="flex items-center" style={{ gap: 6, fontSize: 14, color: 'var(--ink2)' }}>
          <MapPin size={15} aria-hidden />{adresse || 'Keine Adresse hinterlegt'}
        </span>
      </div>
      <div className="relative flex flex-wrap" style={{ gap: 8 }}>
        <button type="button" onClick={() => zuEinheiten()} className="flex items-center whitespace-nowrap cursor-pointer"
          style={{ height: 36, padding: '0 14px', borderRadius: 18, border: 'none', gap: 7, fontSize: 13, fontWeight: 600, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
          <Door weight="fill" size={15} />{wohnLabel}
        </button>
        <KopfKnopf icon={HouseLine} onClick={haeuserFormular}>{haeuser > 0 ? `Häuser (${haeuser})` : 'Haus hinzufügen'}</KopfKnopf>
        <KopfKnopf icon={ChartPieSlice} onClick={berichtscenter}>Berichtscenter</KopfKnopf>
        <KopfKnopf icon={PencilSimple} onClick={() => oeffnePanel('stamm', { bearbeiten: true })}>Bearbeiten</KopfKnopf>
        <Popover
          label="Weitere Aktionen"
          breite={240}
          ausloeser={({ offen, umschalten, ref }) => (
            <button ref={ref} type="button" aria-label="Weitere Aktionen" aria-haspopup="menu" aria-expanded={offen} onClick={umschalten}
              className="flex items-center justify-center cursor-pointer"
              style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: offen ? 'var(--acc-soft)' : 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)', color: offen ? 'var(--acc)' : 'var(--ink)' }}>
              <DotsThree weight="bold" size={18} />
            </button>
          )}
        >
          {schliessen => (
            <>
              <MenueEintrag onClick={() => { schliessen(); neueEinheit(); }}><Plus size={16} />Neue Einheit</MenueEintrag>
              <MenueEintrag onClick={() => { schliessen(); navigate(`/admin/dokumente?objekt=${encodeURIComponent(o.id)}`); }}><FolderSimple size={16} />Dokumente öffnen</MenueEintrag>
              <MenueEintrag onClick={() => { schliessen(); navigate(`/admin/schreiben/neu?objekt=${encodeURIComponent(o.id)}`); }}><PaperPlaneTilt size={16} />Neues Schreiben</MenueEintrag>
              <MenueEintrag onClick={() => { schliessen(); navigate(`/admin/rechnungen?objekt=${encodeURIComponent(o.id)}`); }}><Receipt size={16} />Rechnungen öffnen</MenueEintrag>
              <div style={{ height: 1, margin: '4px 8px', background: 'var(--fill2)' }} />
              <MenueTitel>Einheiten exportieren</MenueTitel>
              <MenueEintrag onClick={() => { schliessen(); exportiere('csv'); }}><FileCsv size={16} />CSV</MenueEintrag>
              <MenueEintrag onClick={() => { schliessen(); exportiere('xlsx'); }}><MicrosoftExcelLogo size={16} />Excel</MenueEintrag>
              <MenueEintrag onClick={() => { schliessen(); exportiere('pdf-table'); }}><Table size={16} />PDF (Tabelle)</MenueEintrag>
              <MenueEintrag onClick={() => { schliessen(); exportiere('pdf-report'); }}><FilePdf size={16} />PDF (Bericht)</MenueEintrag>
              <div style={{ height: 1, margin: '4px 8px', background: 'var(--fill2)' }} />
              <MenueEintrag onClick={() => { schliessen(); setArchivDialog(true); }}>
                {archiviert ? <><ArrowCounterClockwise size={16} />Wiederherstellen</> : <><Archive size={16} />Objekt archivieren</>}
              </MenueEintrag>
              <MenueEintrag gefahr onClick={() => { schliessen(); setLoeschen(true); }}><Trash size={16} />Objekt löschen</MenueEintrag>
            </>
          )}
        </Popover>
      </div>
      {archivDialog && (
        <BestaetigenDialog
          titel={archiviert ? 'Objekt wiederherstellen?' : 'Objekt archivieren?'}
          text={archiviert ? 'Das Objekt erscheint wieder in Liste und Portfolio.' : 'Das Objekt verschwindet aus Liste und Portfolio. Alle Daten bleiben erhalten.'}
          detail={<>„{name}“<br /><span style={{ fontSize: 12, color: 'var(--ink3)' }}>{adresse}</span></>}
          bestaetigenLabel={archiviert ? 'Wiederherstellen' : 'Archivieren'}
          icon={archiviert ? ArrowCounterClockwise : Archive}
          gefahr={false}
          laeuft={loeschtGerade}
          onBestaetigen={() => { void bestaetigeArchiv(); }}
          onAbbrechen={() => setArchivDialog(false)}
        />
      )}
      {loeschen && (
        <BestaetigenDialog
          titel="Objekt löschen?"
          text="Das kann nicht rückgängig gemacht werden."
          detail={<>„{name}“ wird mit allen Einheiten entfernt.<br /><span style={{ fontSize: 12, color: 'var(--ink3)' }}>{adresse}</span></>}
          bestaetigenLabel="Löschen"
          icon={Trash}
          laeuft={loeschtGerade}
          onBestaetigen={() => { void bestaetigeLoeschen(); }}
          onAbbrechen={() => setLoeschen(false)}
        />
      )}
    </header>
  );
}
